import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Button,
  Stack,
  Skeleton,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { monoFontSx } from "../ThemeContext";

const SORT_OPTIONS = [
  { value: "date_desc", label: "Newest first" },
  { value: "date_asc", label: "Oldest first" },
  { value: "cost_desc", label: "Cost: high to low" },
  { value: "cost_asc", label: "Cost: low to high" },
  { value: "mileage_desc", label: "Mileage: high to low" },
  { value: "mileage_asc", label: "Mileage: low to high" },
];

function MaintenanceList({ records, loading, onDelete, onEdit, newlyAddedId }) {
  const [searchText, setSearchText] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("date_desc");

  const hasActiveFilters = Boolean(searchText || dateFrom || dateTo);

  const visibleRecords = useMemo(() => {
    let list = records;

    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase();
      list = list.filter((record) => {
        const type = String(record.service_type || record.serviceType || "").toLowerCase();
        return type.includes(query);
      });
    }

    if (dateFrom) {
      list = list.filter((record) => record.date && record.date >= dateFrom);
    }
    if (dateTo) {
      list = list.filter((record) => record.date && record.date <= dateTo);
    }

    return [...list].sort((a, b) => {
      switch (sortOption) {
        case "date_asc":
          return String(a.date).localeCompare(String(b.date));
        case "cost_asc":
          return (Number(a.cost) || 0) - (Number(b.cost) || 0);
        case "cost_desc":
          return (Number(b.cost) || 0) - (Number(a.cost) || 0);
        case "mileage_asc":
          return (Number(a.mileage) || 0) - (Number(b.mileage) || 0);
        case "mileage_desc":
          return (Number(b.mileage) || 0) - (Number(a.mileage) || 0);
        case "date_desc":
        default:
          return String(b.date).localeCompare(String(a.date));
      }
    });
  }, [records, searchText, dateFrom, dateTo, sortOption]);

  const clearFilters = () => {
    setSearchText("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <Box mt={4}>
      <Typography variant="h6" fontWeight={800} gutterBottom>
        Maintenance History
      </Typography>

      {!loading && records.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2, mt: 1.5, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search service type…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 200, flex: "1 1 200px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            type="date"
            label="From"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Sort by</InputLabel>
            <Select label="Sort by" value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button size="small" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </Box>
      )}

      <Divider sx={{ mb: 1 }} />

      {loading ? (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={60} />
          ))}
        </Stack>
      ) : records.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No maintenance records yet</Typography>
          <Typography variant="body2">
            Log your first service above to start building this car's history.
          </Typography>
        </Box>
      ) : visibleRecords.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No records match your filters</Typography>
          <Button size="small" onClick={clearFilters} sx={{ mt: 1 }}>
            Clear filters
          </Button>
        </Box>
      ) : (
        visibleRecords.map((record, i) => (
          <Box
            key={record.id}
            sx={{
              py: 1.75,
              px: 1,
              mx: -1,
              borderRadius: 1.5,
              borderBottom: i === visibleRecords.length - 1 ? "none" : "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              backgroundColor:
                record.id === newlyAddedId ? (theme) => alpha(theme.palette.success.main, 0.08) : "transparent",
              transition: "background-color 0.15s ease",
              "&:hover": {
                backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.03),
              },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {record.service_type || record.serviceType}
              </Typography>
              <Typography color="text.secondary" variant="body2" sx={monoFontSx}>
                {record.date} · {Number(record.mileage).toLocaleString()} miles
              </Typography>
              {(record.cost != null || record.price != null || record.amount != null) && (
                <Typography color="text.secondary" variant="body2" sx={monoFontSx}>
                  Cost: ${Number(record.cost ?? record.price ?? record.amount).toFixed(2)}
                </Typography>
              )}
            </Box>
            <Box display="flex" flexShrink={0}>
              <IconButton onClick={() => onDelete(record.id)} color="error">
                <DeleteIcon />
              </IconButton>
              <IconButton onClick={() => onEdit(record)} color="primary">
                <EditIcon />
              </IconButton>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

export default MaintenanceList;
